/**
 * Background Site — populates the cinematic landing page with live game data.
 * Builds vertical champion cards and banner-style region cards.
 *
 * @module ui/site
 */

import { bus } from '../core/events.js';
import { getSession, logout, pushSave, lastPushAt } from './auth.js';

const ROLE_LABEL = { tank: 'Tanque', dps: 'Dano', support: 'Suporte' };
const CLASS_ORDER = ['knight', 'rogue', 'ranger', 'pyromancer', 'priest'];

const STAT_LABELS = {
  hp: 'Vida', atk: 'Ataque', mag: 'Magia', def: 'Defesa',
  atkSpd: 'Vel.', crit: 'Crít', dodge: 'Esquiva',
};

/** Region banner art keyed by map id (falls back to a CSS color if missing).
 * The first tiers carry dedicated landing banners (assets/site/banner_*.jpg);
 * the rest reuse their in-game scenario art until banners are generated. */
const REGION_IMG = {
  green_meadows: 'assets/site/banner_green_meadows.jpg',
  dark_forest: 'assets/site/banner_dark_forest.jpg',
  frozen_peaks: 'assets/site/banner_frozen_peaks.jpg',
  lava_caves: 'assets/tiles/lava_caves_bg.png',
  shadow_abyss: 'assets/tiles/shadow_abyss_bg.png',
  drowned_depths: 'assets/tiles/drowned_depths_bg.png',
  celestial_throne: 'assets/tiles/celestial_throne_bg.png',
  vazio_estelar: 'assets/tiles/vazio_estelar_bg.png',
  eternal_necropolis: 'assets/tiles/eternal_necropolis_bg.png',
  crimson_inferno: 'assets/tiles/crimson_inferno_bg.png',
  crystal_caverns: 'assets/tiles/crystal_caverns_bg.png',
  cursed_desert: 'assets/tiles/cursed_desert_bg.png',
  putrid_swamp: 'assets/tiles/putrid_swamp_bg.png',
  mechanical_citadel: 'assets/tiles/mechanical_citadel_bg.png',
};

/** Pick the 4 most relevant base stats for a champion card. */
function pickStats(cls) {
  const keys = ['hp', 'atk', 'def', 'mag', 'atkSpd', 'crit', 'dodge'];
  const base = cls.base ?? {};
  return keys
    .filter((k) => base[k] !== undefined)
    .map((k) => ({ key: k, label: STAT_LABELS[k] ?? k, value: base[k] }))
    .slice(0, 4);
}

/**
 * Build vertical champion cards inside `#pd-site-heroes-grid`.
 * @param {object} classes — data/classes.json
 */
function buildChampionCards(classes) {
  const grid = document.getElementById('pd-site-heroes-grid');
  if (!grid) return;
  grid.innerHTML = '';

  for (const id of CLASS_ORDER) {
    const cls = classes[id];
    if (!cls) continue;

    const statsHtml = pickStats(cls)
      .map((s) => {
        let val = s.value;
        if (s.key === 'atkSpd') val = val.toFixed(2);
        else if (s.key === 'crit' || s.key === 'dodge') val = Math.round(val * 100) + '%';
        return `<span class="pd-champ-stat"><span>${s.label}</span><strong>${val}</strong></span>`;
      })
      .join('');

    const card = document.createElement('div');
    card.className = 'pd-champ';
    card.setAttribute('data-role', cls.role);
    card.innerHTML = `
      <div class="pd-champ-art">
        <span class="pd-champ-role">${ROLE_LABEL[cls.role] ?? cls.role}</span>
        <img src="assets/sprites/portraits/${id}.png" alt="${cls.label}" loading="lazy" />
      </div>
      <div class="pd-champ-body">
        <h3 class="pd-champ-name">${cls.label}</h3>
        <span class="pd-champ-primary">Atributo: ${cls.primary}</span>
        <p class="pd-champ-desc">${cls.mechanic}</p>
        <div class="pd-champ-stats">${statsHtml}</div>
      </div>`;
    grid.appendChild(card);
  }
}

/** How many maps the landing shows inline before the rest move to the overlay. */
const WORLDS_PREVIEW_COUNT = 3;

/** Build one banner-style region card element. */
function regionCard(mapId, map) {
  const [minLv, maxLv] = map.levelRange;
  const monsterCount = map.monsters.length + (map.boss ? 1 : 0);
  const img = REGION_IMG[mapId];
  const monstersHtml = map.monsters
    .map((m) => `<span class="pd-region-monster">${m.label}</span>`)
    .join('');
  const bossHtml = map.boss
    ? `<p class="pd-region-boss">Chefe: <strong>${map.boss.label}</strong></p>`
    : '';

  const card = document.createElement('div');
  card.className = 'pd-region';
  card.setAttribute('data-tier', map.tier);
  card.innerHTML = `
    <div class="pd-region-img"${img ? ` style="background-image:url('${img}')"` : ''}></div>
    <div class="pd-region-info">
      <span class="pd-region-tier">Tier ${map.tier}</span>
      <h3 class="pd-region-name">${map.label}</h3>
      <p class="pd-region-meta">Níveis ${minLv}–${maxLv} · ${monsterCount} criaturas</p>
      <div class="pd-region-monsters">${monstersHtml}</div>
      ${bossHtml}
      <a class="pd-region-explore" href="#pd-pip">Explorar</a>
    </div>`;
  return card;
}

/**
 * Build the region section: a short preview on the landing plus a "ver todos"
 * button that opens a full-screen overlay listing every map — so the page
 * doesn't grow endlessly as new tiers ship.
 * @param {object} maps — data/maps.json
 */
function buildWorldCards(maps) {
  const grid = document.getElementById('pd-site-worlds-grid');
  if (!grid) return;
  grid.innerHTML = '';

  const entries = Object.entries(maps).sort((a, b) => a[1].tier - b[1].tier);

  // Landing preview — only the first few zones.
  for (const [mapId, map] of entries.slice(0, WORLDS_PREVIEW_COUNT)) {
    grid.appendChild(regionCard(mapId, map));
  }

  // "See all maps" button (only when there are more than the preview shows).
  let trigger = document.getElementById('pd-worlds-seeall');
  if (trigger) trigger.remove();
  if (entries.length > WORLDS_PREVIEW_COUNT) {
    trigger = document.createElement('button');
    trigger.id = 'pd-worlds-seeall';
    trigger.className = 'pd-worlds-seeall';
    trigger.type = 'button';
    trigger.textContent = `Ver todos os ${entries.length} mapas →`;
    grid.after(trigger);
    trigger.addEventListener('click', () => openWorldsOverlay(entries));
  }
}

/** Lazily build + open the full-screen "all maps" overlay. */
function openWorldsOverlay(entries) {
  let overlay = document.getElementById('pd-worlds-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'pd-worlds-overlay';
    overlay.className = 'pd-worlds-overlay';
    overlay.innerHTML = `
      <div class="pd-worlds-overlay-head">
        <h2>Todos os Mapas</h2>
        <button class="pd-worlds-overlay-close" type="button" aria-label="Fechar">✕</button>
      </div>
      <div class="pd-worlds-overlay-grid"></div>`;
    document.body.appendChild(overlay);

    const close = () => { overlay.classList.remove('open'); document.body.style.overflow = ''; };
    overlay.querySelector('.pd-worlds-overlay-close').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    const ograte = overlay.querySelector('.pd-worlds-overlay-grid');
    for (const [mapId, map] of entries) ograte.appendChild(regionCard(mapId, map));
  }
  overlay.classList.add('open');
  document.body.style.overflow = 'hidden';
}

/**
 * Swap the hero's static splash for its animated loop when the mp4 ships.
 * The <img> poster stays as instant paint + fallback; the video fades in on
 * top once it can play (and is silently dropped if the file doesn't exist).
 */
function mountHeroVideo() {
  const media = document.querySelector('.pd-site-hero-media');
  if (!media) return;
  const video = document.createElement('video');
  video.muted = true;
  video.loop = true;
  video.autoplay = true;
  video.playsInline = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.poster = 'assets/site/hero_splash.jpg';
  video.src = 'assets/site/hero_loop.mp4';
  video.style.cssText = 'position:absolute;inset:0;opacity:0;transition:opacity .8s ease';
  video.addEventListener('canplay', () => {
    media.appendChild(video);
    video.play().catch(() => {});
    requestAnimationFrame(() => { video.style.opacity = '1'; });
  }, { once: true });
  video.addEventListener('error', () => video.remove(), { once: true });
}

/**
 * TOP HERÓIS — live leaderboard from /api/leaderboard (cloud saves). Shows each
 * account's nick and its strongest party (class + level per hero). Silently
 * degrades to an invitation line when the API is unreachable (e.g. local vite
 * dev without functions) or the board is still empty.
 * @param {object} classes — data/classes.json (for class labels)
 */
async function buildLeaderboard(classes) {
  const el = document.getElementById('pd-leaderboard');
  if (!el) return;
  const empty = (msg) => { el.innerHTML = `<p class="pd-board-empty">${msg}</p>`; };

  let players;
  try {
    const res = await fetch('/api/leaderboard', { cache: 'no-store' });
    if (!res.ok) throw new Error();
    ({ players } = await res.json());
  } catch {
    empty('⚔ O ranking abre junto com os portões — crie sua conta e seja o primeiro!');
    return;
  }
  if (!players?.length) {
    empty('⚔ Nenhum herói no ranking ainda — crie sua conta e seja o primeiro!');
    return;
  }

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
  const partyHtml = (party) => party
    .map((h) => `${esc(classes[h.classId]?.label ?? h.classId)} <b>Nv.${h.level}</b>`)
    .join(' · ');

  el.innerHTML = `
    <table class="pd-lb-table">
      <thead><tr><th>#</th><th>Herói</th><th>Dupla</th><th style="text-align:right">Kills</th></tr></thead>
      <tbody>
        ${players.map((p, i) => `
          <tr class="${i === 0 ? 'pd-lb-first' : ''}">
            <td class="pd-lb-rank">${i + 1}.</td>
            <td class="pd-lb-nick">${esc(p.nick)}</td>
            <td class="pd-lb-party">${partyHtml(p.party)}</td>
            <td class="pd-lb-kills">${p.kills}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

/**
 * Account chip in the top nav. Logged out: nothing (the JOGAR flow opens the
 * login). Logged in: "👤 nick" — clicking it opens a small dropdown with the
 * account's heroes (class + level, read fresh from the local save), the cloud
 * sync status and a logout button.
 * @param {object} classes — data/classes.json (labels + emblem icons)
 */
function initAccountChip(classes) {
  const nav = document.querySelector('.pd-site-nav');
  if (!nav) return;

  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));

  const render = () => {
    nav.querySelector('.pd-nav-account')?.remove();
    const session = getSession();
    if (!session) return;

    const nick = session.nick || session.email.split('@')[0];
    const wrap = document.createElement('div');
    wrap.className = 'pd-nav-account';
    wrap.innerHTML = `
      <button class="pd-nav-account-btn" aria-haspopup="true" aria-expanded="false">
        <span class="pd-nav-account-ic">👤</span> ${esc(nick)}
      </button>
      <div class="pd-nav-account-menu" hidden></div>`;
    nav.appendChild(wrap);

    const btn = wrap.querySelector('.pd-nav-account-btn');
    const menu = wrap.querySelector('.pd-nav-account-menu');

    const heroesHtml = () => {
      let party = [];
      try { party = JSON.parse(localStorage.getItem('pocketdungeon.save') ?? 'null')?.party ?? []; } catch { /* ignore */ }
      if (!party.length) return '<p class="pd-nav-account-empty">Nenhum herói ainda — clique em JOGAR!</p>';
      return party.map((h) => {
        const cls = classes[h.classId];
        return `
          <div class="pd-nav-account-hero">
            <img src="assets/sprites/ui/class_${esc(h.classId)}.png" alt="" />
            <span>${esc(cls?.label ?? h.classId)}</span>
            <b>Nv.${h.level ?? 1}</b>
          </div>`;
      }).join('');
    };

    const openMenu = () => {
      const at = lastPushAt();
      const syncLabel = at ? `☁ sincronizado há ${Math.max(0, Math.round((Date.now() - at) / 60000))} min` : '☁ aguardando primeira sincronização';
      menu.innerHTML = `
        <div class="pd-nav-account-email">${esc(session.email)}</div>
        <div class="pd-nav-account-heroes">${heroesHtml()}</div>
        <div class="pd-nav-account-sync">${syncLabel}</div>
        <button class="pd-nav-account-logout">⏏ Sair da conta</button>`;
      menu.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      menu.querySelector('.pd-nav-account-logout').addEventListener('click', async () => {
        await pushSave().catch(() => {});
        logout();
      });
    };
    const closeMenu = () => { menu.hidden = true; btn.setAttribute('aria-expanded', 'false'); };

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.hidden ? openMenu() : closeMenu();
    });
    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target)) closeMenu();
    });
  };

  render();
  bus.on('auth:login', render);
}

/**
 * Reveal-on-scroll: content blocks start slightly shifted+transparent and ease
 * in as they enter the viewport (CSS does the transition; this just flips the
 * class). Elements already on screen reveal immediately, so nothing "pops".
 */
function initScrollReveal() {
  const targets = document.querySelectorAll('.pd-champ, .pd-region, .pd-board, .pd-site-section-title, .pd-site-section-sub, .pd-site-lore-title, .pd-site-lore-text');
  if (!('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting) {
        e.target.classList.add('pd-reveal-in');
        io.unobserve(e.target);
      }
    }
  }, { threshold: 0.12 });
  targets.forEach((t, i) => {
    t.classList.add('pd-reveal');
    // Small stagger inside grids so card rows cascade instead of thudding in.
    t.style.transitionDelay = `${(i % 5) * 60}ms`;
    io.observe(t);
  });
}

/**
 * Initialise the background site with game data.
 * @param {object} data — { classes, maps }
 */
export function initSite(data) {
  buildChampionCards(data.classes);
  buildWorldCards(data.maps);
  buildLeaderboard(data.classes);
  initAccountChip(data.classes);
  mountHeroVideo();
  initScrollReveal();

  // Keep TOP HERÓIS live: refresh right after each cloud push (your own score
  // just changed) and on a slow poll for everyone else's pushes. The board
  // used to render once at page load and go stale for the whole session.
  bus.on('cloud:pushed', () => buildLeaderboard(data.classes));
  bus.on('auth:login', () => buildLeaderboard(data.classes));
  setInterval(() => buildLeaderboard(data.classes), 90_000);
}
